import React, { useLayoutEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { selectProjectConfig } from '@store/actionSlices/projectConfig'

import { Level } from '@api/building'

import { ChevronSvg, CloseSvg } from '@svg/react'

import { Unit } from '@adUtilities/types/apartment'

import { FILTER_MATCH_LABEL, UNITS_PANEL_TOGGLE_DURATION } from './constants'
import { countUnitsByStatus, getStatusColour } from './level-explorer-util'
import LevelUnitCard from './level-unit-card'
import useLevelDisplayConfig from './use-level-display-config'
import useUnitShortlist from './use-unit-shortlist'

interface LevelUnitsPanelProps {
  level: Level
  isFilterApplied: boolean
  onClose: () => void
  onSeeFloorplateView: () => void
  onSelectUnit: (unit: Unit) => void
}

function LevelUnitsPanel({
  level,
  isFilterApplied,
  onClose,
  onSeeFloorplateView,
  onSelectUnit,
}: LevelUnitsPanelProps) {
  const { statusColors, disableSoldProperty } = useSelector(selectProjectConfig)
  const { levelStagePrefix, availableStatusLabel, availableStatusColour } =
    useLevelDisplayConfig()
  const { isUnitShortlisted, toggleUnitShortlist } = useUnitShortlist()
  const [isExpanded, setIsExpanded] = useState(true)
  const bodyRef = useRef<HTMLDivElement>(null)
  const bodyStartHeight = useRef<number>()

  const summaryText = isFilterApplied
    ? `${level.data.length} ${FILTER_MATCH_LABEL}`
    : `${countUnitsByStatus(level.data, availableStatusLabel)} ${availableStatusLabel}`

  const handleToggleExpanded = () => {
    bodyStartHeight.current = bodyRef.current?.offsetHeight
    setIsExpanded(!isExpanded)
  }

  useLayoutEffect(() => {
    const body = bodyRef.current
    const startHeight = bodyStartHeight.current
    if (!body || startHeight === undefined) {
      return
    }
    body.animate(
      [{ height: `${startHeight}px` }, { height: `${body.offsetHeight}px` }],
      { duration: UNITS_PANEL_TOGGLE_DURATION, easing: 'ease-in-out' }
    )
  }, [isExpanded])

  return (
    <div className="pointer-events-none absolute bottom-5 right-5 top-5 z-20 flex w-[260px] flex-col justify-end xl:right-8 xl:w-[297px]">
      <div className="pointer-events-auto flex max-h-full flex-col overflow-hidden rounded-2xl bg-white shadow-sm desktop-large:rounded-[20px]">
        <div className="relative z-1 shrink-0 shadow-sm">
          <button
            type="button"
            aria-expanded={isExpanded}
            onClick={handleToggleExpanded}
            className="flex w-full flex-col gap-2 px-5 py-4 text-left xl:py-5 desktop-large:gap-2.5"
          >
            <span className="truncate pr-18 text-xl/7 font-bold text-[#111111] xl:pr-20 xl:text-2xl/8 desktop-all:pr-22 desktop-all:text-[26px]/9 desktop-large:pr-24 desktop-large:text-[28px]/10">
              {levelStagePrefix} {level.level}
            </span>
            <span className="flex items-center gap-1.5 text-xs font-semibold uppercase leading-none tracking-[0.04em] text-[#666666] xl:text-sm desktop-all:text-[15px] desktop-large:text-base">
              <span
                className="size-2 shrink-0 rounded-full xl:size-2.5"
                style={{ backgroundColor: availableStatusColour }}
              />
              <span className="truncate">{summaryText}</span>
            </span>
          </button>
          <div className="pointer-events-none absolute right-5 top-4 flex gap-2 xl:top-5">
            <span className="flex size-7 items-center justify-center rounded-full bg-[#111111]/5 text-[#111111]/60 xl:size-8 desktop-all:size-9 desktop-large:size-10">
              <ChevronSvg
                className="size-4 transition-transform duration-300 desktop-all:size-5"
                rotate={isExpanded ? 'down' : 'top'}
                stroke={2}
              />
            </span>
            <button
              type="button"
              title="Close"
              onClick={onClose}
              className="pointer-events-auto flex size-7 items-center justify-center rounded-full bg-[#111111]/5 text-[#111111]/60 xl:size-8 desktop-all:size-9 desktop-large:size-10"
            >
              <CloseSvg
                className="size-2.5 desktop-all:size-3"
                strokeColor="currentColor"
              />
            </button>
          </div>
        </div>
        <div
          ref={bodyRef}
          className={`flex min-h-0 flex-col overflow-hidden ${
            !isExpanded ? 'h-0' : ''
          }`}
        >
          <ul className="custom-scrollbar flex min-h-0 flex-col gap-2.5 overflow-y-auto bg-[#EEEEEE] p-4">
            {level.data.map((unit) => {
              const isAvailable = unit.metas.status === availableStatusLabel
              return (
                <li key={unit.id}>
                  <LevelUnitCard
                    unit={unit}
                    statusColour={getStatusColour(
                      statusColors,
                      unit.metas.status
                    )}
                    isDisabled={disableSoldProperty && !isAvailable}
                    isShortlisted={isUnitShortlisted(unit)}
                    onSelect={() => onSelectUnit(unit)}
                    onToggleShortlist={() => toggleUnitShortlist(unit)}
                  />
                </li>
              )
            })}
          </ul>
        </div>
        <div className="flex shrink-0 flex-col gap-3 px-5 pb-3.5 pt-4 desktop-large:gap-3.5 desktop-large:px-6 desktop-large:pb-4 desktop-large:pt-5">
          <button
            type="button"
            onClick={onSeeFloorplateView}
            className="relative flex h-11 w-full items-center justify-center rounded-[5px] bg-mainColour px-10 text-lg font-semibold leading-none text-white desktop-all:h-12 desktop-large:h-[52px]"
          >
            See floorplate view
            <ChevronSvg className="absolute right-4 h-4 w-4" rotate="right" />
          </button>
          <p className="text-center text-sm leading-[1.3] text-[#666666] desktop-all:text-[15px] desktop-large:text-base">
            <span className="block">Tap anywhere to close</span>
            <span className="block">Tap another level to switch</span>
          </p>
        </div>
      </div>
    </div>
  )
}

export default LevelUnitsPanel
